/**
 * The two A/B tests in the browser (docs/plans/experiments.md), as plain functions with no page and
 * no imports, so scripts/check-experiments.js runs exactly this code beside the server's own
 * (functions/xpGroups.js) and holds the two to each other, vector by vector. utils/experiments.js
 * wires it to the page.
 *
 *   First Answer (fa)  A Full: today's answer. B Short + More: one or two sentences with the heart of
 *                      it, then a More button that brings the full answer. The server decides it
 *                      (functions/experiments.js) from the visitor id sent with a conversation's
 *                      first question; the page only shows More and counts what happens next.
 *   Front Door (fd)    A Starters: today's welcome. B Bare: "Ask anything." over the box, with no
 *                      questions to start with. The page decides it at the welcome's first paint,
 *                      from the switches' copy in meta/publicConfig and the visitor id.
 *
 * The group: the first 4 bytes, big-endian, mod 10,000, of SHA-256("<key>:<visitor id>"); below 5,000
 * is B. The key is the test, the phase (aa for the A/A run, ab live) and the run: "fd_ab1". Nothing
 * here ever shows anyone their group.
 */

export const TESTS = {
  fa: { id: 'fa', name: 'First Answer', arms: { A: 'Full', B: 'Short + More' }, metrics: ['enr', 'more', 'fup', 'on', 'deeper', 'rob', 'left'] },
  fd: { id: 'fd', name: 'Front Door', arms: { A: 'Starters', B: 'Bare' }, metrics: ['enr', 'ask', 'typed'] },
};
// The switches' copy in meta/publicConfig (scripts/set-limits.js writes it beside meta/limits).
export const SWITCH_FIELDS = { fa: ['xpFirstAnswer', 'xpFirstAnswerRun'], fd: ['xpFrontDoor', 'xpFrontDoorRun'] };
export const RUN_RANGE = [1, 20];
// The browser's analytics id (utils/analytics.js apr-vid), as the server accepts it.
export const ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;
const KEY_PATTERN = /^(fa|fd)_(aa|ab)([1-9]|1[0-9]|20)$/;
const SPLIT = 5000;

// ---------------------------------------------------------------------------
// SHA-256 (FIPS 180-4), small and synchronous: the welcome decides at its first paint, and
// crypto.subtle only answers later. scripts/check-experiments.js checks it against Node's.
// ---------------------------------------------------------------------------

const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const utf8 = (text) => new TextEncoder().encode(String(text));

/** SHA-256 of a string's UTF-8 bytes, as its eight 32-bit words (the digest, big-endian). */
export function sha256Words(text) {
  const bytes = utf8(text);
  const len = bytes.length;
  const total = (((len + 9) + 63) >> 6) << 6;
  const m = new Uint8Array(total);
  m.set(bytes);
  m[len] = 0x80;
  const view = new DataView(m.buffer);
  view.setUint32(total - 8, Math.floor((len * 8) / 0x100000000));
  view.setUint32(total - 4, (len * 8) >>> 0);
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const w = new Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
  }
  return h;
}

/** The digest as 64 hex characters. */
export function sha256Hex(text) {
  return sha256Words(text).map((x) => x.toString(16).padStart(8, '0')).join('');
}

// ---------------------------------------------------------------------------
// Keys and groups
// ---------------------------------------------------------------------------

/** "fd_ab1" for a test's mode (1 A/A, 2 live) and run; null when off or not a test. */
export function keyFor(test, mode, run) {
  if (!TESTS[test] || !(mode === 1 || mode === 2)) return null;
  if (!(Number.isInteger(run) && run >= RUN_RANGE[0] && run <= RUN_RANGE[1])) return null;
  return `${test}_${mode === 1 ? 'aa' : 'ab'}${run}`;
}

/** 0..9999 from SHA-256("<key>:<id>"). */
export function bucketFor(key, id) {
  return sha256Words(`${key}:${id}`)[0] % 10000;
}

/** B below 5,000, A otherwise. */
export function armFor(key, id) {
  return bucketFor(key, id) < SPLIT ? 'B' : 'A';
}

/** A public number as the server reads it (functions/limits.js publicNumber): whole, pulled into its range; null when it isn't one. */
function wholeIn(value, [min, max]) {
  let n = null;
  if (typeof value === 'number' && Number.isFinite(value)) n = value;
  else if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value.trim()))) n = Number(value.trim());
  return n === null ? null : Math.min(max, Math.max(min, Math.floor(n)));
}

/**
 * Both tests' switches from meta/publicConfig (or a copy of it): { fa: { mode, run }, fd: { mode, run } },
 * off (0) and run 1 for anything missing or unreadable, read the way the server reads them.
 */
export function cleanSwitches(source) {
  const s = source && typeof source === 'object' ? source : {};
  const out = {};
  for (const test of Object.keys(SWITCH_FIELDS)) {
    const [modeField, runField] = SWITCH_FIELDS[test];
    const mode = wholeIn(s[modeField], [0, 2]);
    const run = wholeIn(s[runField], RUN_RANGE);
    out[test] = { mode: mode === null ? 0 : mode, run: run === null ? 1 : run };
  }
  return out;
}

/**
 * Front Door at the welcome's first paint: { key, arm, bare } for a visit in the test, else null.
 * In it: a guest (no account yet: the team always signs in, and an account's role isn't known at
 * first paint), whose browser counts visits (a visitor id: not Do Not Track or Global Privacy
 * Control), isn't marked as the team's, and didn't land with a bank question (/q/, ?q=); while the
 * switches say the test is on. bare only while it is live and the group is B.
 */
export function frontDoorChoice({ switches, vid, guest = false, internal = false, landing = false }) {
  if (!switches || !switches.fd) return null;
  const key = keyFor('fd', switches.fd.mode, switches.fd.run);
  if (!key || !guest || internal || landing) return null;
  if (typeof vid !== 'string' || !ID_PATTERN.test(vid)) return null;
  const arm = armFor(key, vid);
  return { key, arm, bare: switches.fd.mode === 2 && arm === 'B' };
}

/**
 * First Answer's word in an answer's metadata (functions/experiments.js firstAnswerMeta), made safe:
 * { k, arm, short } for an answer in the test, { short: true, team: true } for a team member's own
 * pick, else null.
 */
export function firstAnswerMeta(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.team === true && raw.short === true) return { short: true, team: true };
  const m = typeof raw.k === 'string' ? KEY_PATTERN.exec(raw.k) : null;
  if (!m || m[1] !== 'fa' || (raw.arm !== 'A' && raw.arm !== 'B')) return null;
  return { k: raw.k, arm: raw.arm, short: raw.short === true };
}

/**
 * The analytics event for one moment of a test, or null when anything is off its list:
 * { t: 'xp', x, k, arm, m }. The server checks the group again against the batch's visitor id.
 */
export function buildXpEvent({ x, k, arm, m } = {}) {
  const match = typeof k === 'string' ? KEY_PATTERN.exec(k) : null;
  if (!TESTS[x] || !match || match[1] !== x) return null;
  if (arm !== 'A' && arm !== 'B') return null;
  if (!TESTS[x].metrics.includes(m)) return null;
  return { t: 'xp', x, k, arm, m };
}

// ---------------------------------------------------------------------------
// What a moment adds to the counts (utils/experiments.js keeps the records in the tab's
// sessionStorage, so only the tab that counted a visit or a first answer counts what follows it)
// ---------------------------------------------------------------------------

// Going on from a first answer: each counted once. More and a follow-up are going on ("on", with
// the first of them); Go deeper and Ask the real Rob are not leaving either.
const ENGAGED = ['more', 'fup', 'deeper', 'rob'];

/**
 * One moment of a first answer in the test, given the tab's record of it ({ k, arm, sent }):
 * { record, events }, the record as it now stands and the metrics to send now (none when they were
 * sent before). `left` counts only before they went on: someone who went on within the 15 seconds
 * did not leave. Going on after being counted as left is counted as itself (a follow-up is a
 * follow-up); `left` stays.
 */
export function firstAnswerStep(record, metric) {
  if (!record || typeof record !== 'object' || !record.k || !Array.isArray(record.sent)) return { record, events: [] };
  const sent = new Set(record.sent);
  const events = [];
  const add = (m) => {
    if (sent.has(m)) return false;
    sent.add(m);
    events.push(m);
    return true;
  };
  if (metric === 'left') {
    if (!ENGAGED.some((m) => sent.has(m))) add('left');
  } else if (ENGAGED.includes(metric) && add(metric)) {
    if (metric === 'more' || metric === 'fup') add('on');
  }
  return { record: { ...record, sent: [...sent] }, events };
}

/**
 * A question asked in a visit Front Door counted ({ k, arm, enr, ask?, typed? }): { seen, events }.
 * `ask` the first time; `typed` the first time it is in their own words (typed or spoken, never a
 * tap on a question to start with).
 */
export function frontDoorAskStep(seen, { typed = false } = {}) {
  if (!seen || typeof seen !== 'object' || !seen.enr || !seen.k) return { seen, events: [] };
  const next = { ...seen };
  const events = [];
  if (!seen.ask) {
    next.ask = true;
    events.push('ask');
  }
  if (typed && !seen.typed) {
    next.typed = true;
    events.push('typed');
  }
  return { seen: next, events };
}
